import { Fragment, h } from 'preact'
import { useState, useEffect, useLayoutEffect } from 'preact/hooks'
import dayjs from 'dayjs'

import cx from '../../shared/classes'
import { useStateValue } from '../../state-management/store'
import { CALENDAR_DATE_FORMAT, APPOINTMENT_TIME_FORMAT, APPOINTMENT_DATE_FORMAT } from '../../shared/const'
import CalendarBody from './calendar-body'
import { Spinner } from '../spinner'
import { softGreen } from '../../shared/colors'
import CalendarMonthSelector from './calendar-month-selector'
import CalendarWeekSelector from './calendar-week-selector'
import CalendarDaysSelector from './calendar-days-selector'
import removeDatetimeOffset from '../../shared/removeDatetimeOffset'

import * as style from './calendar.css'

interface Props {
    selectedDate: string
    daysWithAvailableAppointmentSlots: string[]
    loading: boolean
    nextAvailableAppointmentShown: boolean
    canShowNoAvailableAppointmentsMsg: boolean
    onSetDate: (day: string) => void
    onSetDateRange: (from: string, to: string) => void
    setNextAvailableAppointment: (appointmentTime: string) => void
    loaderMessage?: string
}

const Calendar: preact.FunctionalComponent<Props> = ({
    selectedDate,
    daysWithAvailableAppointmentSlots,
    loading,
    nextAvailableAppointmentShown,
    canShowNoAvailableAppointmentsMsg,
    onSetDate,
    onSetDateRange,
    setNextAvailableAppointment,
    loaderMessage = 'Looking for available appointments',
}) => {
    const [{ selectedAppointmentDate, availableTimes }, dispatch] = useStateValue()
    const today = new Date()
    const dayJsToday = dayjs(today)

    const handleResetAppointmentDateTime = async () => {
        dispatch({
            type: 'SET_SELECTED_APPOINTMENT_TIME',
            appointmentTime: '',
        })
        dispatch({
            type: 'SET_SELECTED_APPOINTMENT_DATE',
            appointmentDate: '',
        })
    }

    const getDateFrom = (date: Date): string => {
        const firstDayInMonth = dayjs(date).startOf('month').startOf('week')
        if (firstDayInMonth.isBefore(dayJsToday)) {
            return dayJsToday.format(CALENDAR_DATE_FORMAT)
        }

        return firstDayInMonth.format(CALENDAR_DATE_FORMAT)
    }

    const getDateTo = (date: Date): string => {
        return dayjs(date).endOf('month').endOf('week').format(CALENDAR_DATE_FORMAT)
    }

    const initialCurrentDate = selectedDate ? dayjs(selectedDate).toDate() : today
    const [currentDate, setCurrentDate] = useState<Date>(initialCurrentDate)
    const [dateFrom, setDateFrom] = useState<string>('')
    const [dateTo, setDateTo] = useState<string>('')

    const showNoAvailableAppointmentMsg =
        canShowNoAvailableAppointmentsMsg &&
        !loading &&
        daysWithAvailableAppointmentSlots.filter((day) => dayjs(day).get('month') <= dayjs(currentDate).get('month'))
            .length === 0

    useLayoutEffect(() => {
        setDateFrom(getDateFrom(initialCurrentDate))
        setDateTo(getDateTo(initialCurrentDate))
    }, [])

    useEffect(() => {
        if (dateFrom && dateTo) {
            onSetDateRange(dateFrom, dateTo)
        }
    }, [dateFrom, dateTo])

    useEffect(() => {
        setDateFrom(getDateFrom(currentDate))
        setDateTo(getDateTo(currentDate))
    }, [currentDate])

    useEffect(() => {
        if (selectedDate !== '') {
            setCurrentDate(dayjs(selectedDate).toDate())
        }
    }, [selectedDate])

    const onDateClick = (day: dayjs.Dayjs): void => {
        const formattedDay = day.format(CALENDAR_DATE_FORMAT) ? day.format(CALENDAR_DATE_FORMAT) : ''
        onSetDate(formattedDay)
    }

    const getDateStatus = (day: dayjs.Dayjs) => {
        const formattedFullDate = dayjs(day).format(CALENDAR_DATE_FORMAT)
        const hasAvailableSlots = daysWithAvailableAppointmentSlots.includes(formattedFullDate) && !loading
        const isDisabled = dayjs(day).isBefore(dayJsToday, 'day') || !hasAvailableSlots || loading

        return { isDisabled, hasAvailableSlots }
    }

    // TODO: find a better solution for back to calendar view CTA in mobile self sch. [AMP-2970]
    const renderBackToCalendar = () => (
        <div className={style.backButtonOverlay} onClick={handleResetAppointmentDateTime} />
    )

    const renderNextAvailableAppointment = () => {
        if (availableTimes[0] && !selectedAppointmentDate) {
            const nextAvailableAppointmentTime = availableTimes[0]
            const nextAvailableAppointmentTimeWithoutOffset = removeDatetimeOffset(nextAvailableAppointmentTime)

            return (
                <div className={style.nextAppWrapper}>
                    <div className={style.nextApp}>Next Available Appointment</div>
                    <div
                        className={style.nextAppLink}
                        onClick={() => setNextAvailableAppointment(nextAvailableAppointmentTime)}
                    >{`${dayjs(nextAvailableAppointmentTime).format(APPOINTMENT_DATE_FORMAT)} @ ${dayjs(
                        nextAvailableAppointmentTimeWithoutOffset
                    ).format(APPOINTMENT_TIME_FORMAT)}`}</div>
                </div>
            )
        }
        return ''
    }

    const renderNoAvailableAppointmentsMessage = () => {
        const monthName = dayjs(currentDate).format('MMMM')

        return (
            <div className={style.noAvailableAppointmentsWrapper}>
                <div className={style.noAvailableAppointments}>
                    <div
                        className={style.noAvailableAppointmentsText}
                    >{`Sorry, no ${monthName} appointments available`}</div>
                </div>
            </div>
        )
    }

    const renderCalendarLargeView = () => {
        return (
            <Fragment>
                <CalendarMonthSelector currentDate={currentDate} setCurrentDate={setCurrentDate} />
                <CalendarBody
                    currentDate={currentDate}
                    selectedDate={selectedDate}
                    onDateClick={onDateClick}
                    getDateStatus={getDateStatus}
                />
                {showNoAvailableAppointmentMsg && renderNoAvailableAppointmentsMessage()}
            </Fragment>
        )
    }

    const renderCalendarSmallView = () => {
        return (
            <div className={style.body}>
                <CalendarMonthSelector currentDate={currentDate} setCurrentDate={setCurrentDate} />
                {renderBackToCalendar()}
                <CalendarWeekSelector
                    selectedDate={selectedDate}
                    daysWithAvailableAppointmentSlots={daysWithAvailableAppointmentSlots}
                    currentDate={currentDate}
                    setCurrentDate={setCurrentDate}
                    onDateClick={onDateClick}
                    getDateStatus={getDateStatus}
                />
            </div>
        )
    }

    const renderCalendarSmallViewExpanded = () => {
        return (
            <div className={style.expandedWrapper}>
                <div>
                    <CalendarMonthSelector currentDate={currentDate} setCurrentDate={setCurrentDate} />
                </div>
                <CalendarBody
                    currentDate={currentDate}
                    selectedDate={selectedDate}
                    onDateClick={onDateClick}
                    getDateStatus={getDateStatus}
                />
                {showNoAvailableAppointmentMsg && renderNoAvailableAppointmentsMessage()}
                <div className={style.helpText}>
                    <div className={style.oval} />
                    <span>Appointments available</span>
                </div>
            </div>
        )
    }

    const renderCalendarExtraSmallView = () => {
        return (
            <div className={style.body}>
                {renderBackToCalendar()}
                <CalendarDaysSelector
                    selectedDate={selectedDate}
                    daysWithAvailableAppointmentSlots={daysWithAvailableAppointmentSlots}
                    numberOfDays={5}
                    currentDate={currentDate}
                    setCurrentDate={setCurrentDate}
                    onDateClick={onDateClick}
                    getDateStatus={getDateStatus}
                />
            </div>
        )
    }

    return (
        <Fragment>
            <section
                className={cx({
                    [style.calendarContainer]: true,
                    [style.calendarDisabled]: loading,
                })}
            >
                {!nextAvailableAppointmentShown && (
                    <div className={style.nextAvailableAppSection}>{renderNextAvailableAppointment()}</div>
                )}

                <div className={style.calendar}>{renderCalendarLargeView()}</div>
                <div className={style.calendarSmall}>
                    {selectedAppointmentDate ? renderCalendarSmallView() : renderCalendarSmallViewExpanded()}
                </div>
                <div className={style.calendarXSmall}>
                    {selectedAppointmentDate ? renderCalendarExtraSmallView() : renderCalendarSmallViewExpanded()}
                </div>
            </section>
            {loading && (
                <div className={style.spinnerContainer}>
                    <Spinner color={softGreen} />
                    <div className={style.spinnerText}>{loaderMessage}</div>
                </div>
            )}
        </Fragment>
    )
}

export default Calendar
